import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { FaGlobe, FaCheck, FaCog } from 'react-icons/fa';
import { useTheme } from '../theme/ThemeContext';
import { useOrg } from '../hooks/useOrg';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

const AVATAR_COLORS = ['#f59e0b', '#8b5cf6', '#0066cc', '#10b981', '#ef4444', '#ec4899', '#06b6d4'];

const ROLE_BADGE_COLORS = {
  owner:  '#f59e0b',
  admin:  '#8b5cf6',
  member: '#3b82f6',
  viewer: '#6b7280',
};

function getAvatarColor(name = '') {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function OrgAvatar({ name = '?', logoUrl = null, size = 36, fontSize = 15 }) {
  const [imgError, setImgError] = useState(false);
  if (logoUrl && !imgError) {
    return (
      <Image
        source={{ uri: logoUrl }}
        style={[styles.avatar, { width: size, height: size, borderRadius: size / 2, backgroundColor: '#e5e7eb' }]}
        onError={() => setImgError(true)}
      />
    );
  }
  const color = getAvatarColor(name);
  return (
    <View style={[styles.avatar, { width: size, height: size, borderRadius: size / 2, backgroundColor: color }]}>
      <Text style={[styles.avatarLetter, { fontSize }]}>{name[0]?.toUpperCase() || '?'}</Text>
    </View>
  );
}

export default function OrgSwitcher() {
  const { orgs, activeOrg, mode, switchOrg, setAllOrgsMode } = useOrg();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ bottom: 0, left: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const inTrigger = triggerRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inTrigger && !inDropdown) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const visibleOrgs = orgs.filter(o => !o.is_personal);

  if (visibleOrgs.length === 0) return null;

  const handleOpen = () => {
    if (open) { setOpen(false); return; }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      const isRTL = i18n.language === 'he';
      if (isRTL) {
        // In RTL, sidebar is on the right — open dropdown to the left of the sidebar
        setDropdownPos({ bottom: window.innerHeight - rect.bottom, right: window.innerWidth - rect.left + 8 });
      } else {
        setDropdownPos({ bottom: window.innerHeight - rect.bottom, left: rect.right + 8 });
      }
    }
    setOpen(true);
  };

  const isActiveOrg = (orgId) => mode === 'org' && activeOrg?.id === orgId;

  const bg = theme.bgCard ?? '#1c1c1e';
  const border = theme.border;

  const dropdown = open ? ReactDOM.createPortal(
    <div ref={dropdownRef} style={{
        position: 'fixed',
        bottom: dropdownPos.bottom,
        ...(dropdownPos.right != null ? { right: dropdownPos.right } : { left: dropdownPos.left }),
        zIndex: 9999,
        minWidth: 240,
        maxWidth: 280,
        borderRadius: 14,
        border: `1px solid ${border}`,
        backgroundColor: bg,
        paddingTop: 8,
        paddingBottom: 8,
        boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
      }}>
        {/* Individual orgs */}
        {visibleOrgs.map(org => (
          <div
            key={org.id}
            onClick={() => { switchOrg(org.id); setOpen(false); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', cursor: 'pointer', borderRadius: 8, margin: '0 4px',
              backgroundColor: isActiveOrg(org.id) ? '#0066cc18' : 'transparent',
            }}
            onMouseEnter={e => { if (!isActiveOrg(org.id)) e.currentTarget.style.backgroundColor = theme.bgSurface; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = isActiveOrg(org.id) ? '#0066cc18' : 'transparent'; }}
          >
            <OrgAvatar name={org.name} logoUrl={org.logo_url} size={32} fontSize={13} />
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <div style={{ color: theme.textPrimary, fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{org.name}</div>
              {org.my_role && <div style={{ color: ROLE_BADGE_COLORS[org.my_role], fontSize: 11, textTransform: 'capitalize', marginTop: 1 }}>{org.my_role}</div>}
            </div>
            {isActiveOrg(org.id) && <FaCheck size={12} color="#0066cc" />}
          </div>
        ))}

        {/* All Orgs */}
        {visibleOrgs.length > 1 && (
          <>
            <div style={{ height: 1, backgroundColor: border, margin: '4px 12px' }} />
            <div
              onClick={() => { setAllOrgsMode(); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px', cursor: 'pointer', borderRadius: 8, margin: '0 4px',
                backgroundColor: mode === 'all' ? '#0066cc18' : 'transparent',
              }}
              onMouseEnter={e => { if (mode !== 'all') e.currentTarget.style.backgroundColor = theme.bgSurface; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = mode === 'all' ? '#0066cc18' : 'transparent'; }}
            >
              <View style={[styles.avatar, { width: 32, height: 32, borderRadius: 16, backgroundColor: '#3b82f6' }]}>
                <FaGlobe size={14} color="#fff" />
              </View>
              <span style={{ color: theme.textPrimary, fontSize: 13, fontWeight: 500, flex: 1 }}>{t('app.orgSwitcher.allOrgs')}</span>
              {mode === 'all' && <FaCheck size={12} color="#0066cc" />}
            </div>
          </>
        )}

        {/* Org settings */}
        {mode === 'org' && (
          <>
            <div style={{ height: 1, backgroundColor: border, margin: '4px 12px' }} />
            <div
              onClick={() => { navigate('/org/settings'); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px', cursor: 'pointer', borderRadius: 8, margin: '0 4px',
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = theme.bgSurface; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <View style={[styles.avatar, { width: 32, height: 32, borderRadius: 16, backgroundColor: border }]}>
                <FaCog size={14} color={theme.textSecondary} />
              </View>
              <span style={{ color: theme.textSecondary, fontSize: 13, fontWeight: 500, flex: 1 }}>{t('app.orgSwitcher.orgSettings')}</span>
            </div>
          </>
        )}
    </div>,
    document.body
  ) : null;

  const orgLabel = mode === 'all' ? t('app.orgSwitcher.allOrgs') : (activeOrg?.name || t('app.orgSwitcher.allOrgs'));

  return (
    <View ref={triggerRef} style={styles.container}>
      <TouchableOpacity style={styles.triggerCircle} onPress={handleOpen} activeOpacity={0.8}>
        {mode === 'all' ? (
          <View style={[styles.avatar, { width: 36, height: 36, borderRadius: 18, backgroundColor: '#3b82f6' }]}>
            <FaGlobe size={16} color="#fff" />
          </View>
        ) : (
          <OrgAvatar name={activeOrg?.name || 'O'} logoUrl={activeOrg?.logo_url} size={36} />
        )}
        <span className="nav-label" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <span style={{ color: theme.textPrimary, fontWeight: 500, fontSize: 13 }}>{orgLabel}</span>
          <span style={{ color: theme.textMuted, fontWeight: 400, fontSize: 10, marginTop: 1 }}>{t('app.orgSwitcher.switchOrganization')}</span>
        </span>
      </TouchableOpacity>
      {dropdown}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { position: 'relative' },
  triggerCircle: { cursor: 'pointer', flexDirection: 'row', alignItems: 'center' },
  avatar: { justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  avatarLetter: { color: '#fff', fontWeight: '700' },
});
